import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { bilingualLines } from '../../i18n/script';
import { useSiteLanguage } from '../../i18n/useSiteLanguage';
import { courseApi } from '../../services/courseApi';
import { promotionCodeApi } from '../../services/promotionCodeApi';

interface ActivityOption {
  id: string;
  titleCN: string | null;
  titleEN: string | null;
  status: string;
}

interface FormData {
  code: string;
  description: string;
  discountType: 'FIXED_AMOUNT' | 'PERCENTAGE';
  discountValue: string;
  maxUsesPerUser: string;
  validFrom: string;
  validUntil: string;
  activityIds: string[];
  isActive: boolean;
}

export default function AdminPromotionCodeForm() {
  const siteLanguage = useSiteLanguage();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, getToken } = useAuth();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [activities, setActivities] = useState<ActivityOption[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(true);

  // Track whether code has been used to restrict certain edits
  const [hasUsages, setHasUsages] = useState(false);

  const [formData, setFormData] = useState<FormData>({
    code: '',
    description: '',
    discountType: 'FIXED_AMOUNT',
    discountValue: '',
    maxUsesPerUser: '',
    validFrom: '',
    validUntil: '',
    activityIds: [],
    isActive: true,
  });

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  // Fetch activities list
  useEffect(() => {
    const fetchActivities = async () => {
      if (!isAuthenticated) return;
      try {
        setLoadingActivities(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';
        const response = await fetch(`${apiUrl}/admin/activities`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Failed to fetch activities');
        const result = await response.json();
        // Exclude archived (INACTIVE) in-person activities from selection.
        const activeActivities: ActivityOption[] = result.success
          ? (result.data || []).filter((a: ActivityOption) => a.status !== 'INACTIVE')
          : [];
        // Recorded courses are eligible too (promo discounts apply to USD course
        // checkouts). The public list returns live, sellable courses.
        let courseOptions: ActivityOption[] = [];
        try {
          const courses = await courseApi.listCourses();
          courseOptions = courses.map((c) => ({
            id: c.id,
            titleCN: c.titleCN,
            titleEN: c.titleEN,
            status: 'ACTIVE',
          }));
        } catch {
          // Non-critical: in-person activities still selectable if courses fail.
        }
        setActivities([...activeActivities, ...courseOptions]);
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load activities';
        setError(errorMessage);
      } finally {
        setLoadingActivities(false);
      }
    };
    fetchActivities();
  }, [isAuthenticated, getToken]);

  // Fetch promotion code data if editing
  useEffect(() => {
    if (!isEditing || !isAuthenticated) return;

    const fetchCode = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        if (!token) throw new Error('No access token');
        const data = await promotionCodeApi.getById(id!, token);

        setHasUsages((data.usages?.length ?? 0) > 0);

        // Convert dates to datetime-local format
        const toLocalDatetime = (dateStr: string | null) => {
          if (!dateStr) return '';
          const d = new Date(dateStr);
          // Format: YYYY-MM-DDTHH:MM
          const pad = (n: number) => String(n).padStart(2, '0');
          return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
        };

        setFormData({
          code: data.code,
          description: data.description || '',
          discountType: data.discountType,
          discountValue: String(data.discountValue),
          maxUsesPerUser: data.maxUsesPerUser !== null ? String(data.maxUsesPerUser) : '',
          validFrom: toLocalDatetime(data.validFrom),
          validUntil: toLocalDatetime(data.validUntil),
          activityIds:
            data.activities?.map((a: { activity: { id: string } }) => a.activity.id) || [],
          isActive: data.isActive,
        });
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load promotion code';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchCode();
  }, [isEditing, id, isAuthenticated, getToken]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => ({ ...prev, [name]: checked }));
    } else if (name === 'code') {
      // Auto-uppercase, restrict to valid chars
      setFormData((prev) => ({ ...prev, code: value.toUpperCase().replace(/[^A-Z0-9-]/g, '') }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleActivityToggle = (activityId: string) => {
    setFormData((prev) => ({
      ...prev,
      activityIds: prev.activityIds.includes(activityId)
        ? prev.activityIds.filter((id) => id !== activityId)
        : [...prev.activityIds, activityId],
    }));
  };

  const handleSelectAllActivities = () => {
    if (formData.activityIds.length === activities.length) {
      setFormData((prev) => ({ ...prev, activityIds: [] }));
    } else {
      setFormData((prev) => ({
        ...prev,
        activityIds: activities.map((a) => a.id),
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.code.trim()) {
      setError('Code is required');
      return;
    }
    if (!/^[A-Z0-9-]{1,20}$/.test(formData.code)) {
      setError('Code must be 1-20 characters, only A-Z, 0-9, and hyphens');
      return;
    }

    const discountValue = Number(formData.discountValue);
    if (!discountValue || discountValue <= 0) {
      setError('Discount value must be greater than 0');
      return;
    }
    if (formData.discountType === 'PERCENTAGE' && discountValue > 100) {
      setError('Percentage discount cannot exceed 100');
      return;
    }

    if (formData.activityIds.length === 0) {
      setError('Please select at least one activity');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const token = await getToken();
      if (!token) throw new Error('No access token');

      const payload: Record<string, unknown> = {
        code: formData.code,
        description: formData.description || undefined,
        discountType: formData.discountType,
        discountValue: discountValue,
        maxUsesPerUser: formData.maxUsesPerUser ? Number(formData.maxUsesPerUser) : null,
        validFrom: formData.validFrom ? new Date(formData.validFrom).toISOString() : null,
        validUntil: formData.validUntil ? new Date(formData.validUntil).toISOString() : null,
        activityIds: formData.activityIds,
      };

      if (isEditing) {
        payload.isActive = formData.isActive;
        await promotionCodeApi.update(
          id!,
          payload as Parameters<typeof promotionCodeApi.update>[1],
          token
        );
      } else {
        await promotionCodeApi.create(
          payload as Parameters<typeof promotionCodeApi.create>[0],
          token
        );
      }

      navigate('/admin/promotion-codes');
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save promotion code';
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/promotion-codes" className="text-gray-500 hover:text-gray-700">
              &larr; Back
            </Link>
            <h1 className="text-2xl font-bold text-gray-900">
              {isEditing ? 'Edit Promotion Code' : 'Create Promotion Code'}
            </h1>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">
              Dismiss
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Code & Discount */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Code & Discount</h2>

            {hasUsages && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                This code has been used. Code, discount type, and discount value cannot be changed.
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Promotion Code *
                </label>
                <input
                  type="text"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  disabled={hasUsages}
                  maxLength={20}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary font-mono uppercase disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="e.g., WELCOME10"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  1-20 characters. Only letters, numbers, and hyphens.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  placeholder="Optional description..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Discount Type *
                  </label>
                  <select
                    name="discountType"
                    value={formData.discountType}
                    onChange={handleChange}
                    disabled={hasUsages}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="FIXED_AMOUNT">Fixed Amount ($)</option>
                    <option value="PERCENTAGE">Percentage (%)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.discountType === 'FIXED_AMOUNT'
                      ? 'Amount in cents *'
                      : 'Percentage (1-100) *'}
                  </label>
                  <input
                    type="number"
                    name="discountValue"
                    value={formData.discountValue}
                    onChange={handleChange}
                    disabled={hasUsages}
                    min={1}
                    max={formData.discountType === 'PERCENTAGE' ? 100 : undefined}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder={
                      formData.discountType === 'FIXED_AMOUNT'
                        ? 'e.g., 500 for $5.00'
                        : 'e.g., 10 for 10%'
                    }
                    required
                  />
                  {formData.discountType === 'FIXED_AMOUNT' && formData.discountValue && (
                    <p className="text-xs text-gray-500 mt-1">
                      = ${(Number(formData.discountValue) / 100).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Limits & Validity */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Limits & Validity</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Uses Per User
                </label>
                <input
                  type="number"
                  name="maxUsesPerUser"
                  value={formData.maxUsesPerUser}
                  onChange={handleChange}
                  min={1}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  placeholder="Leave empty for unlimited"
                />
                <p className="text-xs text-gray-500 mt-1">
                  How many times each user can use this code. Leave empty for unlimited.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid From</label>
                  <input
                    type="datetime-local"
                    name="validFrom"
                    value={formData.validFrom}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional start date</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Valid Until
                  </label>
                  <input
                    type="datetime-local"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional end date</p>
                </div>
              </div>

              {isEditing && (
                <div className="flex items-center gap-3 pt-2">
                  <input
                    type="checkbox"
                    name="isActive"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={handleChange}
                    className="rounded border-gray-300 text-primary focus:ring-primary"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                    Active
                  </label>
                </div>
              )}
            </div>
          </div>

          {/* Activity Selection */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Eligible Activities
              {activities.length > 0 && (
                <span className="text-sm font-normal text-gray-500 ml-2">
                  ({formData.activityIds.length}/{activities.length} selected)
                </span>
              )}
            </h2>

            {loadingActivities ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Loading activities...</p>
              </div>
            ) : activities.length === 0 ? (
              <p className="text-sm text-gray-500">No activities found.</p>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleSelectAllActivities}
                  className="text-sm text-primary hover:underline mb-3"
                >
                  {formData.activityIds.length === activities.length
                    ? 'Deselect All'
                    : 'Select All'}
                </button>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {activities.map((activity) => (
                    <label
                      key={activity.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        formData.activityIds.includes(activity.id)
                          ? 'border-primary bg-primary/5'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.activityIds.includes(activity.id)}
                        onChange={() => handleActivityToggle(activity.id)}
                        className="rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <div className="flex-1">
                        <span className="text-sm text-gray-900">
                          {bilingualLines(activity.titleCN, activity.titleEN, siteLanguage)[0]}
                        </span>
                        {bilingualLines(activity.titleCN, activity.titleEN, siteLanguage)[1] && (
                          <span className="text-sm text-gray-500 ml-2">
                            ({bilingualLines(activity.titleCN, activity.titleEN, siteLanguage)[1]})
                          </span>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Submit */}
          <div className="flex items-center gap-4">
            <Link
              to="/admin/promotion-codes"
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className={`px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors ${
                submitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {submitting ? 'Saving...' : isEditing ? 'Update Code' : 'Create Code'}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
